// src/features/sso-auth/services/authService.ts

import axios from 'axios';

axios.interceptors.request.use((config) => {
  const accessToken = localStorage.getItem('access_token');
  config.headers = config.headers || {};
  if (accessToken) {
    config.headers['Authorization'] = `Bearer ${accessToken}`;
  } else {
    delete config.headers['Authorization'];
  }
  return config;
});

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

export interface UserJWTPayload {
  id: string;
  email: string;
  name: string;
  role: string;
  iat: number; // issued at
  exp: number; // expiration
}

export interface LoginResponse {
  url: string;
  data: string;
  timestamp: string;
  testing: boolean;
  access_token: string | null;
  refresh_token: string | null;
}

export interface CheckTokenResponse {
  access_token: string;
  refresh_token: string;
}

export interface RefreshTokenResponse {
  access_token: string;
  refresh_token: string;
}

export class AuthService {
  static async initiateLogin(): Promise<LoginResponse> {
    const response = await axios.get(`${BACKEND_URL}/api/v1/user/login`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    localStorage.setItem('data', JSON.stringify(response.data.data));
    return response.data;
  }

  static async checkToken(query: string, data: string): Promise<CheckTokenResponse> {
    const response = await axios.post(`${BACKEND_URL}/api/v1/user/login/check-token`, {
      query,
      data,
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    // No guardar directamente en localStorage, dejar que el store lo maneje
    return response.data;
  }

  static async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    const response = await axios.post(`${BACKEND_URL}/api/v1/user/login/refresh-token`, {
      refresh_token: refreshToken,
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }

  static getUrlParams(): { query: string | null; data: string | null; error?: string | null } {
    const urlParams = new URLSearchParams(window.location.search);
    // Obtener y parsear el valor de 'data' del localStorage
    let stateValue = null;
    const localData = localStorage.getItem('data');
    if (localData) {
      try {
        stateValue = JSON.parse(localData);
      } catch (e) {
        stateValue = localData;
      }
    }
    return {
      query: `code=${urlParams.get('code')}` +
        `&state=${urlParams.get('state')}` +
        `&client_info=${urlParams.get('client_info')}` +
        `&session_state=${urlParams.get('session_state')}`,
      data: stateValue
    };
  }
}

// Función para decodificar el JWT (solo para desarrollo/testing)
export function decodeJWT(token: string): UserJWTPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = JSON.parse(atob(parts[1]));
    return payload as UserJWTPayload;
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}
